import React, { useState, useEffect, useCallback } from 'react';
import ClinicInfoText from './ClinicInfoText';
import ClinicInfoForm from './ClinicInfoForm';
import ClinicInfoSkeleton from './ClinicInfoSkeleton';
import settingsService from '../../../../../services/settings.service';
import { useEyeconxStore } from '../../../../../store/eyeconxStore';
import { showError, showSuccess } from '../../../../../utils/ecxSwal';
import ClinicSettingsSectionHeader from '../subparts/ClinicSettingsSectionHeader';
import ClinicSettingsCancelButton from '../subparts/ClinicSettingsCancelButton';

const ClinicInfo = () => {
    const [clinicInfo, setClinicInfo] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [logoDimensions, setLogoDimensions] = useState({ width: 0, height: 0 });
    const clinicId = useEyeconxStore((state) => state.user.clinicId);

    const getImageDimensions = useCallback((url) => {
        const img = new Image();
        img.onload = () => setLogoDimensions({ width: img.width, height: img.height });
        img.src = url;
    }, []);

    useEffect(() => {
        const fetchClinicInfo = async () => {
            try {
                setIsLoading(true);
                const res = await settingsService.getClinicById(clinicId);
                setClinicInfo(res.data);
                if (res.data.logo) {
                    getImageDimensions(res.data.logo);
                }
            } catch (error) {
                console.error('Error fetching clinic info:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchClinicInfo();
    }, [clinicId, getImageDimensions]);

    const handleSave = async (data) => {
        const clinicUpdateData = {
            id: data.id,
            name: data.name,
            email: data.email,
            phone: data.phone.replace(/\D/g, ''),
            signature: data.signature,
            website: data.website,
            street: data.street,
            city: data.city,
            postalCode: data.postalCode,
            province: data.province,
            country: data.country,
        };

        setIsSaving(true);

        try {
            await settingsService.updateClinic(clinicId, clinicUpdateData);
            setClinicInfo({ ...clinicInfo, ...data });
            setIsEditing(false);

            if (data.logo) {
                getImageDimensions(data.logo);
            }

            await showSuccess({
                title: 'Clinic Information Saved',
                text: 'Your clinic details were updated successfully.',
            });
        } catch (error) {
            console.error('Error updating clinic info:', error);
            showError({
                title: 'Save Failed',
                text: 'Unable to update clinic information. Please try again.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <>
                <ClinicSettingsSectionHeader
                    title="Clinic Information"
                    icon="bi-building"
                    subtitle="View and update your clinic profile, contact details, and address"
                />
                <ClinicInfoSkeleton />
            </>
        );
    }

    if (!clinicInfo) {
        return (
            <div className="alert alert-danger text-center">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Failed to load clinic information.
            </div>
        );
    }

    return (
        <>
            <ClinicSettingsSectionHeader
                title="Clinic Information"
                icon="bi-building"
                subtitle="View and update your clinic profile, contact details, and address"
                actions={(
                    isEditing ? (
                        <ClinicSettingsCancelButton onClick={() => setIsEditing(false)} disabled={isSaving} />
                    ) : (
                        <button
                            className="btn btn-sm btn-light-primary rounded-pill"
                            onClick={() => setIsEditing(true)}
                            type="button"
                        >
                            <i className="bi bi-pencil me-1"></i>
                            Edit
                        </button>
                    )
                )}
            />

            {isEditing ? (
                <ClinicInfoForm
                    clinicInfo={clinicInfo}
                    onSubmit={handleSave}
                    getImageDimensions={getImageDimensions}
                    logoDimensions={logoDimensions}
                    isSaving={isSaving}
                />
            ) : (
                <ClinicInfoText clinicInfo={clinicInfo} logoDimensions={logoDimensions} />
            )}
        </>
    );
};

export default ClinicInfo;
